"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.Value = void 0;
const util_1 = require("../util");
/*
 * This Symbol is used to mark classes created by the Value mixin as belonging to the dom.Value
 * class hierarchy. This allows the `className instanceof dom.Value` test to work as expected.
 */
const _DOM_VALUE_SIGNET = Symbol("ion.dom.Value");
/**
 * A mixin[1] that allows each DOM class to effectively extend two different parent classes:
 *   1. The corresponding native JS data type (dom.String extends String, dom.Integer extends Number, etc.)
 *   2. A new class constructed by the DomValue method which provides functionality common to all
 *      DOM elements. This includes storing/accessing an Ion data type and annotations, as well as
 *      convenience methods for converting from Ion data types to JS data types.
 *
 * [1] https://www.typescriptlang.org/docs/handbook/mixins.html
 *
 * @param BaseClass             A parent type for the newly constructed class to extend.
 * @param ionType               The Ion data type that will be associated with new instances of the constructed class.
 * @param fromJsConstructor     Calls the class's primary constructor with a Javascript value after
 *                              performing configured validation and adaptation logic.
 * @constructor
 */
function Value(BaseClass, ionType, fromJsConstructor) {
    const newClass = class extends BaseClass {
        /* TODO:
         *  Ideally, this mixin's constructor would require subclasses to specify the desired annotations list
         *  for the value being created as an argument. Something like:
         *     constructor(annotations: string[], ...args: any[]) {
         *         super(args);
         *         this._setAnnotations(annotations);
         *     }
         *  Unfortunately, Typescript requires[1] that mixins have a single constructor which accepts a
         *  single spread parameter. This means that we can't statically enforce this; callers would need
         *  to "just know" to pass an annotations list as the first element of an arguments array.
         *  For now, subclasses are expected to call `this._setAnnotations(...)` after the constructor completes.
         *  This avoids the runtime costs that would be associated with the constant slicing/inspection of
         *  values in the `...args` list to detect annotations.
         *
         *  [1] https://github.com/Microsoft/TypeScript/issues/14126
         */
        constructor(...args) {
            super(...args);
            this._ionType = ionType;
            this._ionAnnotations = [];
            // Setting the 'enumerable' attribute of these properties to `false` prevents them
            // from appearing in the iterators returned by Object.keys(), Object.entries(), etc.
            // This guarantees that users iterating over the fields of a struct or values in a list
            // will see only Values from the source data or that they have created themselves.
            Object.defineProperty(this, "_ionType", { enumerable: false });
            Object.defineProperty(this, "_ionAnnotations", { enumerable: false });
        }
        _unsupportedOperation(functionName) {
            throw new Error(`Value#${functionName}() is not supported by Ion type ${this.getType().name}`);
        }
        getType() {
            return this._ionType;
        }
        // Class expressions (like this mixin) cannot have private or protected methods.
        _setAnnotations(annotations) {
            this._ionAnnotations = annotations;
        }
        getAnnotations() {
            if (this._ionAnnotations === null) {
                return [];
            }
            return this._ionAnnotations;
        }
        isNull() {
            return false;
        }
        booleanValue() {
            this._unsupportedOperation("booleanValue");
        }
        numberValue() {
            this._unsupportedOperation("numberValue");
        }
        bigIntValue() {
            this._unsupportedOperation("bigIntValue");
        }
        decimalValue() {
            this._unsupportedOperation("decimalValue");
        }
        stringValue() {
            this._unsupportedOperation("stringValue");
        }
        dateValue() {
            this._unsupportedOperation("dateValue");
        }
        timestampValue() {
            this._unsupportedOperation("timestampValue");
        }
        uInt8ArrayValue() {
            this._unsupportedOperation("uInt8ArrayValue");
        }
        fieldNames() {
            this._unsupportedOperation("fieldNames");
        }
        fields() {
            this._unsupportedOperation("fields");
        }
        allFields() {
            this._unsupportedOperation("allFields");
        }
        elements() {
            this._unsupportedOperation("elements");
        }
        get(...pathElements) {
            this._unsupportedOperation("get");
        }
        getAll(...pathElements) {
            this._unsupportedOperation("getAll");
        }
        as(ionValueType) {
            if (this instanceof ionValueType) {
                return this;
            }
            throw new Error(`${this.constructor.name} is not an instance of ${ionValueType.name}`);
        }
        writeTo(writer) {
            this._unsupportedOperation("writeTo");
        }
        deleteField(name) {
            this._unsupportedOperation("deleteField");
        }
        /**
         * Helper method to be implemented by each subclass of dom.Value with individual equivalence logic.
         */
        // Class expressions (like this mixin) cannot have private or protected methods.
        _valueEquals(other, options = {
            epsilon: null,
            ignoreAnnotations: false,
            ignoreTimestampPrecision: false,
            onlyCompareIon: true,
            coerceNumericType: false,
        }) {
            this._unsupportedOperation("_valueEquals");
        }
        /**
         * Implementation of the dom.Value interface method equals()
         */
        equals(other, options = { epsilon: null }) {
            let onlyCompareIon = false;
            if (other instanceof Value) {
                onlyCompareIon = true;
            }
            return this._valueEquals(other, {
                onlyCompareIon: onlyCompareIon,
                ignoreTimestampPrecision: true,
                ignoreAnnotations: true,
                epsilon: options.epsilon,
                coerceNumericType: true,
            });
        }
        /**
         * Implementation of the dom.Value interface method ionEquals()
         */
        ionEquals(other, options = {
            epsilon: null,
            ignoreAnnotations: false,
            ignoreTimestampPrecision: false,
        }) {
            if (!options.ignoreAnnotations) {
                if (!(other instanceof Value)) {
                    return false;
                }
                let actualAnnotations = this.getAnnotations();
                let expectedAnnotations = other.getAnnotations();
                if (actualAnnotations.length !== expectedAnnotations.length) {
                    return false;
                }
                for (let i = 0; i < actualAnnotations.length; i++) {
                    if (actualAnnotations[i].localeCompare(expectedAnnotations[i]) !== 0) {
                        return false;
                    }
                }
            }
            let ion_options = {
                onlyCompareIon: true,
                ignoreTimestampPrecision: options.ignoreTimestampPrecision,
                epsilon: options.epsilon,
                coerceNumericType: false,
            };
            return this._valueEquals(other, ion_options);
        }
        // Returns the IonType associated with a particular dom.Value subclass. Useful for testing.
        static _getIonType() {
            return ionType;
        }
        // Verifies that the provided jsValue's type is supported by this class's constructor
        // before using it to create a new instance of this class. In some cases, performs
        // adaptation logic (e.g. unboxing boxed primitives) on jsValue before invoking the constructor.
        // As a static mixin method, this method will be inherited by all subclasses of dom.Value.
        // Value subtypes requiring more complex conversion logic (e.g. Struct, List) are free
        // to override it.
        static _fromJsValue(jsValue, annotations) {
            return fromJsConstructor.construct(this, jsValue, annotations);
        }
    };
    // Classes created using this mixin will each have this static property.
    // Because its name is a Symbol[1], it cannot be referenced outside of the class except
    // by code in this file (which has access to the constant '_DOM_VALUE_SIGNET').
    // See the Symbol.hasInstance property that is defined on the Value class at the bottom
    // of this file for details.
    //
    // [1] https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Symbol
    Object.defineProperty(newClass, _DOM_VALUE_SIGNET, {
        writable: false,
        enumerable: false,
        value: _DOM_VALUE_SIGNET,
    });
    return newClass;
}
exports.Value = Value;
// When testing whether a given object is an `instanceof Value`, this function
// will test for the presence of the _DOM_VALUE_SIGNET Symbol on that object's constructor.
Object.defineProperty(Value, Symbol.hasInstance, {
    get: () => (instance) => {
        return ((0, util_1._hasValue)(instance) &&
            (0, util_1._hasValue)(instance.constructor) &&
            _DOM_VALUE_SIGNET in instance.constructor &&
            instance.constructor[_DOM_VALUE_SIGNET] === _DOM_VALUE_SIGNET);
    },
});
